import { c as _c } from "react/compiler-runtime";
import { Trash } from '@byway/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';
import { DialogTrigger } from 'react-aria-components';
import { Controller, FormProvider, type UseFormReturn, useForm, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '~/components/Button';
import { Divider } from '~/components/Divider';
import { FlexibleDatesPanel } from '~/components/FlexibleDatesPanel';
import { Stack } from '~/components/Layout/Stack';
import { Popover } from '~/components/Popover';
import { getCountryIdFromDestinationInUrl } from '~/constants/countries';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import { usePathname, useRouter } from '~/i18n/navigation';
import { type CreateTripTab, usePlaygroundActions, usePlaygroundPreferences, usePlaygroundUIState } from '~/stores/playgroundStore';
import { trackEvent } from '~/utils';
import { PlaygroundDateSelector } from '../PlaygroundDateSelector/PlaygroundDateSelector';
import { PlaygroundStartEndSelector } from '../PlaygroundStartEndSelector/PlaygroundStartEndSelector';
import styles from './PlaygroundIndexForm.module.css';
const ValidationSchema = z.object({
  origin: z.object({
    id: z.number(),
    n: z.string(),
    c: z.object({
      n: z.string()
    })
  }).nullable(),
  endPlace: z.object({
    id: z.number(),
    n: z.string(),
    c: z.object({
      n: z.string()
    })
  }).nullable(),
  dates: z.object({
    type: z.enum(['nights', 'calendar']),
    value: z.union([z.object({
      startDate: z.string(),
      endDate: z.string()
    }), z.number()])
  }).nullable()
});
type PlaygroundFormSchemaType = z.infer<typeof ValidationSchema>;
interface PlaygroundIndexFormProps {
  variant?: 'module' | 'page';
  isHeroCta?: boolean;
  travelType?: CreateTripTab;
}
export function PlaygroundIndexForm({
  variant = 'page',
  isHeroCta = false,
  travelType = 'round-trip'
}: PlaygroundIndexFormProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const router = useRouter();
  const {
    setOriginId,
    setIsLoading
  } = usePlaygroundActions();
  const preferences = usePlaygroundPreferences();
  const {
    isLoading
  } = usePlaygroundUIState();
  const pathname = usePathname();
  const countryName = pathname?.split('/')[2];
  const countryId = countryName ? getCountryIdFromDestinationInUrl(countryName) : null;
  const isSubmitEnabled = Boolean(preferences.nights || preferences.startDate && preferences.endDate);
  const onSubmit = async (data: PlaygroundFormSchemaType) => {
    const {
      origin,
      dates
    } = data;
    if (!origin) return;
    trackEvent('playground_get_started', {
      event_category: 'Playground',
      event_label: 'playground_get_started',
      playground_form_sub: data
    });
    setOriginId(origin.id);
    setIsLoading(true);
    const url = new URL('/map/build', window.location.origin);
    url.searchParams.set('origin', origin.id.toString());

    // Only add countryId to URL if it's not null/undefined
    if (countryId && isHeroCta) {
      url.searchParams.set('countryId', countryId);
    }
    if (dates) {
      if (dates.type === 'nights') {
        url.searchParams.set('nights', dates.value.toString());
      } else if (dates.type === 'calendar') {
        const {
          startDate,
          endDate
        } = dates.value as {
          startDate: string;
          endDate: string;
        };
        url.searchParams.set('startDate', startDate);
        url.searchParams.set('endDate', endDate);
      }
    }
    router.push(url.pathname + url.search);
  };
  const playgroundIndexForm = useForm<PlaygroundFormSchemaType>({
    resolver: zodResolver(ValidationSchema),
    defaultValues: {
      origin: null,
      endPlace: null,
      dates: preferences.nights ? {
        type: 'nights',
        value: preferences.nights
      } : preferences.startDate && preferences.endDate ? {
        type: 'calendar',
        value: {
          startDate: preferences.startDate,
          endDate: preferences.endDate
        }
      } : null
    },
    mode: 'onChange'
  });
  const origin_0 = playgroundIndexForm.watch('origin');
  const isFormEnabled = Boolean(isSubmitEnabled && origin_0?.id);
  return <FormProvider {...playgroundIndexForm}>
      <form ref={formRef} onSubmit={playgroundIndexForm.handleSubmit(onSubmit)}>
        <PlaygroundIndexPanel variant={variant} originForm={playgroundIndexForm} isFormEnabled={isFormEnabled} isLoading={isLoading} travelType={travelType} />
      </form>
    </FormProvider>;
}
type PlaygroundStartProps = {
  originForm: UseFormReturn<PlaygroundFormSchemaType>;
  isFormEnabled: boolean;
  variant?: 'module' | 'page';
  isLoading: boolean;
  travelType?: CreateTripTab;
};
export function PlaygroundIndexPanel(t0) {
  const $ = _c(79);
  const {
    originForm,
    isFormEnabled,
    variant: t1,
    isLoading,
    travelType: t2
  } = t0;
  const variant = t1 === undefined ? "page" : t1;
  const travelType = t2 === undefined ? "round-trip" : t2;
  const isModule = variant === "module";
  const t = useTranslations("content.map");
  const isDesktop = useIsDesktop();
  const {
    reset
  } = useFormContext();
  const {
    setStartDate,
    setEndDate,
    setNights,
    closeDrawer
  } = usePlaygroundActions();
  const {
    isDrawerOpen,
    drawerItem
  } = usePlaygroundUIState();
  let t3;
  if ($[0] !== reset || $[1] !== setEndDate || $[2] !== setNights || $[3] !== setStartDate) {
    t3 = () => {
      reset();
      setStartDate(null);
      setEndDate(null);
      setNights(null);
    };
    $[0] = reset;
    $[1] = setEndDate;
    $[2] = setNights;
    $[3] = setStartDate;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleReset = t3;
  let t4;
  if ($[5] !== isModule) {
    t4 = clsx(styles.contentAndCta, {
      [styles.moduleVariant]: isModule
    });
    $[5] = isModule;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== isModule) {
    t5 = clsx(styles.heading, {
      [styles.moduleVariant]: isModule
    });
    $[7] = isModule;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t || $[10] !== travelType) {
    t6 = travelType === "round-trip" ? t("form_where_from") : t("form_where_from_to");
    $[9] = t;
    $[10] = travelType;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = <h1 className={t5}>{t6}</h1>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== isModule || $[16] !== t) {
    t8 = !isModule && <p className={styles.body}>{t("form_origin_help")}</p>;
    $[15] = isModule;
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const t9 = isDesktop ? "horizontal" : "vertical";
  let t10;
  if ($[18] !== originForm.control) {
    t10 = <Controller control={originForm.control} name="origin" render={_temp} />;
    $[18] = originForm.control;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== originForm.control || $[21] !== travelType) {
    t11 = travelType === "one-way-trip" && <Controller control={originForm.control} name="endPlace" render={_temp2} />;
    $[20] = originForm.control;
    $[21] = travelType;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t9) {
    t12 = <Stack variant={t9} space="--by-space-300">{t10}{t11}</Stack>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t9;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12 || $[28] !== t7 || $[29] !== t8) {
    t13 = <div className={styles.content}>{t7}{t8}{t12}</div>;
    $[27] = t12;
    $[28] = t7;
    $[29] = t8;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== isModule) {
    t14 = clsx(styles.heading, {
      [styles.moduleVariant]: isModule
    });
    $[31] = isModule;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t) {
    t15 = t("form_how_long");
    $[33] = t;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t14 || $[36] !== t15) {
    t16 = <h2 className={t14}>{t15}</h2>;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== isModule || $[39] !== t) {
    t17 = !isModule && <p className={styles.body}>{t("form_how_long_help")}</p>;
    $[38] = isModule;
    $[39] = t;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  const t18 = isDesktop && isDrawerOpen && drawerItem === "dates";
  let t19;
  if ($[41] !== closeDrawer) {
    t19 = isOpen => {
      if (!isOpen) {
        closeDrawer();
      }
    };
    $[41] = closeDrawer;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== originForm.control) {
    t20 = <Controller control={originForm.control} name="dates" render={_temp3} />;
    $[43] = originForm.control;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== closeDrawer || $[46] !== isDesktop || $[47] !== t) {
    t21 = isDesktop && <Popover ariaLabel={t("form_edit_dates_label")} maxWidth={600} offset={25} placement="right" isDismissable={true} onOpenChange={isOpen_0 => {
      if (!isOpen_0) {
        closeDrawer();
      }
    }} close={true} noHeader={true}><FlexibleDatesPanel layout="horizontal" /></Popover>;
    $[45] = closeDrawer;
    $[46] = isDesktop;
    $[47] = t;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== t18 || $[50] !== t19 || $[51] !== t20 || $[52] !== t21) {
    t22 = <DialogTrigger isOpen={t18} onOpenChange={t19}>{t20}{t21}</DialogTrigger>;
    $[49] = t18;
    $[50] = t19;
    $[51] = t20;
    $[52] = t21;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== t16 || $[55] !== t17 || $[56] !== t22) {
    t23 = <div className={styles.content}>{t16}{t17}{t22}</div>;
    $[54] = t16;
    $[55] = t17;
    $[56] = t22;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Divider variant="horizontal" />;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Trash decorative={true} />;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== t) {
    t26 = t("form_clear_all");
    $[60] = t;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== handleReset || $[63] !== t26) {
    t27 = <Button onPress={handleReset} variant="asLink">{t25}{t26}</Button>;
    $[62] = handleReset;
    $[63] = t26;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  const t28 = !isFormEnabled;
  let t29;
  if ($[65] !== t) {
    t29 = t("form_start_creating");
    $[65] = t;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== isLoading || $[68] !== t28 || $[69] !== t29) {
    t30 = <Button isDisabled={t28} type="submit" isLoading={isLoading}>{t29}</Button>;
    $[67] = isLoading;
    $[68] = t28;
    $[69] = t29;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] !== t27 || $[72] !== t30) {
    t31 = <div className={styles.bottomButtonBar}>{t27}{t30}</div>;
    $[71] = t27;
    $[72] = t30;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] !== t13 || $[75] !== t23 || $[76] !== t31 || $[77] !== t4) {
    t32 = <div className={t4}>{t13}{t23}{t24}{t31}</div>;
    $[74] = t13;
    $[75] = t23;
    $[76] = t31;
    $[77] = t4;
    $[78] = t32;
  } else {
    t32 = $[78];
  }
  return t32;
}
function _temp3(t0) {
  const {
    field: field_1
  } = t0;
  return <PlaygroundDateSelector onChange={field_1.onChange} />;
}
function _temp2(t0) {
  const {
    field: field_0
  } = t0;
  return <PlaygroundStartEndSelector variant="end" {...field_0} />;
}
function _temp(t0) {
  const {
    field
  } = t0;
  return <PlaygroundStartEndSelector variant="start" {...field} />;
}